import { useState, useEffect } from 'react';
import axios from 'axios';
import { PackageCheck, Truck, RefreshCw, Loader, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IncomingScreen = () => {
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // { supplier, description, brand, qty }
  const [busy, setBusy] = useState(false);

  const fetchIncoming = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/incoming`);
      setGroups(res.data || {});
    } catch (err) {
      console.error('Error loading incoming:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncoming();
  }, []);

  const confirmReceived = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await axios.post(`${API}/incoming/receive`, {
        supplier: target.supplier,
        description: target.description,
      });
      await fetchIncoming();
      toast.success(`Received · ${target.description}`, {
        description:
          '📦 Next: pack the customer\'s order, then open the Pickup tab and call/text them to let them know it\'s ready.',
        duration: 9000,
      });
      setTarget(null);
    } catch (err) {
      console.error('Receive failed:', err);
      toast.error('Failed to mark received — please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center text-gray-500 gap-2">
        <Loader className="animate-spin" size={18} /> Loading incoming…
      </div>
    );
  }

  const supplierNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  const totalItems = supplierNames.reduce((sum, b) => sum + groups[b].length, 0);

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#0066FF]/10 flex items-center justify-center">
              <Truck className="text-[#0066FF]" size={20} />
            </div>
            <div>
              <h2
                className="text-2xl font-bold text-gray-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Incoming Orders
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                {totalItems === 0
                  ? 'Nothing on the way right now.'
                  : `${totalItems} item${totalItems !== 1 ? 's' : ''} on the way across ${supplierNames.length} supplier${supplierNames.length !== 1 ? 's' : ''}.`}
              </p>
            </div>
          </div>
          <button
            data-testid="refresh-incoming-button"
            onClick={fetchIncoming}
            className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-semibold rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {totalItems === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Inbox className="text-gray-400" size={26} />
          </div>
          <p className="text-gray-600 font-semibold mb-1">No items currently incoming</p>
          <p className="text-sm text-gray-500">
            When an admin marks an item as ordered in the Report tab, it will
            appear here grouped by supplier.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {supplierNames.map((supplier) => {
            const items = groups[supplier];
            return (
              <div
                key={supplier}
                data-testid={`incoming-supplier-${supplier}`}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                  <h3
                    className="text-lg font-bold text-gray-900 truncate"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {supplier}
                  </h3>
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-500">
                    {items.length} item{items.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <ul className="divide-y divide-gray-100">
                  {items.map((it) => {
                    const key = `${it.supplier}::${it.description}`;
                    return (
                      <li
                        key={key}
                        data-testid={`incoming-item-${key}`}
                        className="px-5 py-3 flex items-center gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate" title={it.description}>
                            {it.description}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {it.brand ? `${it.brand} · ` : ''}qty {it.qty}
                          </p>
                        </div>
                        <button
                          data-testid={`receive-button-${key}`}
                          onClick={() => setTarget(it)}
                          className="bg-[#10B981] hover:bg-[#059669] text-white font-semibold rounded-lg h-10 px-4 text-sm transition-colors flex items-center gap-2 flex-shrink-0"
                        >
                          <PackageCheck size={14} />
                          Received
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!target}
        title="Mark item as received?"
        message={
          target ? (
            <span>
              Confirm <strong>{target.description}</strong>
              {target.brand ? ` (${target.brand})` : ''} — qty {target.qty} — has
              physically arrived in store.
            </span>
          ) : null
        }
        hint="Pack the customer's order, then open the Pickup tab and call/text the customer(s) waiting on this product."
        confirmLabel="Yes, received"
        cancelLabel="Not yet"
        accent="success"
        busy={busy}
        onConfirm={confirmReceived}
        onClose={() => !busy && setTarget(null)}
      />
    </div>
  );
};

export default IncomingScreen;
